import React, { useState, useMemo, useEffect, memo } from 'react';
import { DeploymentPipeline } from '../DeploymentPipeline';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Page,
  Header,
  Content,
  Progress,
  LogViewer,
  CodeSnippet,
  Link,
} from '@backstage/core-components';
import { useApi, useRouteRef, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  scaffolderApiRef,
  useTaskEventStream,
} from '@backstage/plugin-scaffolder-react';
import { rootRouteRef } from '@backstage/plugin-scaffolder';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DateTime, Interval } from 'luxon';

function extractGithubUrl(value: string | undefined, kind: 'pull' | 'actions'): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.hostname !== 'github.com') return undefined;
    const pattern = kind === 'pull'
      ? /^\/[^/]+\/[^/]+\/pull\/\d+$/
      : /^\/[^/]+\/[^/]+\/actions\/runs\/\d+$/;
    return pattern.test(url.pathname) ? url.toString() : undefined;
  } catch {
    return undefined;
  }
}

// ─── Design tokens (Stripe-inspired) ─────────────────────────────────────────

const S = {
  bg:           '#f6f9fc',
  white:        '#ffffff',
  border:       '#e3e8ee',
  shadow:       '0 2px 5px rgba(50,50,93,0.08), 0 1px 3px rgba(0,0,0,0.05)',
  shadowSm:     '0 1px 3px rgba(50,50,93,0.06)',
  text:         '#0a2540',
  textSub:      '#425466',
  textMuted:    '#8898aa',
  accent:       '#635bff',
  accentHover:  '#4f46e5',
  green:        '#1ea672',
  greenBg:      '#ecfdf5',
  greenText:    '#047857',
  red:          '#e5484d',
  redBg:        '#fff0f0',
  redText:      '#b91c1c',
  amber:        '#d97706',
  amberBg:      '#fffbeb',
  amberText:    '#92400e',
  blue:         '#2563eb',
  blueBg:       '#eff6ff',
  blueText:     '#1d4ed8',
  radius:       '8px',
  radiusSm:     '5px',
  mono:         '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
} as const;

// ─── Status pill ──────────────────────────────────────────────────────────────

const StatusPill = ({ status }: { status: string }) => {
  const map: Record<string, { bg: string; text: string; dot: string }> = {
    completed:  { bg: S.greenBg,  text: S.greenText, dot: S.green },
    failed:     { bg: S.redBg,    text: S.redText,   dot: S.red   },
    processing: { bg: S.blueBg,   text: S.blueText,  dot: S.blue  },
    cancelled:  { bg: S.amberBg,  text: S.amberText, dot: S.amber },
  };
  const c = map[status] ?? { bg: '#f3f4f6', text: '#4b5563', dot: '#9ca3af' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 9px', borderRadius: 20,
      background: c.bg, color: c.text,
      fontSize: 12, fontWeight: 600, letterSpacing: '0.02em',
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
      {status}
    </span>
  );
};

// ─── Step status dot ──────────────────────────────────────────────────────────

const StepDot = ({ status }: { status: string }) => {
  const size = 16;
  if (status === 'processing') {
    return <CircularProgress size={size} style={{ color: S.accent, flexShrink: 0 }} />;
  }
  if (status === 'completed') {
    return (
      <span style={{ width: size, height: size, borderRadius: '50%', background: S.green, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
          <path d="M1.5 4.5L3.5 6.5L7.5 2.5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (status === 'failed' || status === 'cancelled') {
    return (
      <span style={{ width: size, height: size, borderRadius: '50%', background: S.red, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
          <path d="M2.5 2.5L6.5 6.5M6.5 2.5L2.5 6.5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  return <span style={{ width: size, height: size, borderRadius: '50%', background: S.border, border: `2px solid #c8d0da`, flexShrink: 0, display: 'inline-block' }} />;
};

// ─── Live duration ticker ─────────────────────────────────────────────────────

const StepTimeTicker = memo(({ startedAt, endedAt }: { startedAt?: string; endedAt?: string }) => {
  const [label, setLabel] = React.useState('');
  React.useEffect(() => {
    if (!startedAt) return undefined;
    const tick = () => {
      const end = endedAt ? DateTime.fromISO(endedAt) : DateTime.local();
      const ms = Interval.fromDateTimes(DateTime.fromISO(startedAt), end).toDuration().valueOf();
      const s = Math.floor(ms / 1000);
      const m = Math.floor(s / 60);
      setLabel(m > 0 ? `${m}m ${s % 60}s` : `${s}s`);
    };
    tick();
    if (!endedAt) {
      const id = setInterval(tick, 1000);
      return () => clearInterval(id);
    }
    return undefined;
  }, [startedAt, endedAt]);
  return <span style={{ color: S.textMuted, fontSize: 11, fontFamily: S.mono }}>{label}</span>;
});

// ─── Stripe card wrapper ──────────────────────────────────────────────────────

const Card = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <Box style={{ background: S.white, border: `1px solid ${S.border}`, borderRadius: S.radius, boxShadow: S.shadow, overflow: 'hidden', ...style }}>
    {children}
  </Box>
);

const CardHeader = ({ label, right }: { label: string; right?: React.ReactNode }) => (
  <Box px={2} py={1.5} display="flex" alignItems="center" style={{ borderBottom: `1px solid ${S.border}` }}>
    <Typography style={{ color: S.textMuted, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
      {label}
    </Typography>
    {right && <Box ml="auto">{right}</Box>}
  </Box>
);

// ─── Terminal chrome bar ──────────────────────────────────────────────────────

const TerminalBar = ({ stepName, status, ticker }: { stepName: string; status: string; ticker?: React.ReactNode }) => (
  <Box style={{
    background: '#161b22', borderBottom: '1px solid #30363d',
    padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
  }}>
    <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#ff5f57', display: 'inline-block' }} />
    <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#febc2e', display: 'inline-block' }} />
    <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#28c840', display: 'inline-block' }} />
    <Box style={{ width: 1, height: 14, background: '#30363d', margin: '0 4px', flexShrink: 0 }} />
    <Typography style={{ color: '#8b949e', fontSize: 12, fontFamily: S.mono, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {stepName}
    </Typography>
    <Box flexGrow={1} />
    {ticker && <span style={{ color: '#8b949e', fontSize: 11, fontFamily: S.mono, marginRight: 8 }}>{ticker}</span>}
    <StatusPill status={status} />
  </Box>
);

// ─── Stripe button ────────────────────────────────────────────────────────────

const StripeBtn = ({
  onClick, disabled, variant = 'primary', children,
}: {
  onClick: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'danger';
  children: React.ReactNode;
}) => {
  const [hover, setHover] = useState(false);
  const base: React.CSSProperties = {
    width: '100%', padding: '8px 14px', borderRadius: S.radiusSm,
    fontSize: 13, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
    border: 'none', transition: 'background 0.15s, opacity 0.15s',
    opacity: disabled ? 0.45 : 1,
  };
  const styles =
    variant === 'primary'
      ? { ...base, background: hover && !disabled ? S.accentHover : S.accent, color: '#fff', border: 'none' }
      : { ...base, background: 'transparent', color: S.red, border: `1px solid ${hover && !disabled ? S.red : '#fca5a5'}` };
  return (
    <button style={styles} disabled={disabled} onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
    >
      {children}
    </button>
  );
};

// ─── Value renderer for parameters ───────────────────────────────────────────

const renderValue = (value: unknown): React.ReactNode => {
  if (value === null || value === undefined) return <em style={{ color: S.textMuted }}>—</em>;
  if (typeof value === 'boolean') return <code style={{ fontFamily: S.mono, fontSize: 12 }}>{String(value)}</code>;
  if (typeof value === 'object') return <CodeSnippet language="json" text={JSON.stringify(value, null, 2)} showLineNumbers={false} />;
  return <span style={{ fontFamily: S.mono, fontSize: 13, color: S.text }}>{String(value)}</span>;
};

// ─── Backend step type ────────────────────────────────────────────────────────

interface BackendStep {
  id: string;
  name: string;
  status: 'completed' | 'failed' | 'running';
  startTime: string;
  endTime?: string;
  duration: number;
  output: string;
  error?: string;
}

const formatMs = (ms: number | undefined, from?: string, to?: string) => {
  const dur = ms ?? (from && to ? new Date(to).getTime() - new Date(from).getTime() : undefined);
  if (!dur || dur <= 0) return '—';
  const s = Math.floor(dur / 1000);
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
};

// ─── Main page ────────────────────────────────────────────────────────────────

export const CustomScaffolderTaskPage = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const scaffolderApi = useApi(scaffolderApiRef);
  const rootPath = useRouteRef(rootRouteRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [selectedStepId, setSelectedStepId] = useState<string | undefined>();
  const [cancelling, setCancelling] = useState(false);
  const [backendFallback, setBackendFallback] = useState<{
    prUrl?: string;
    actionsRunUrl?: string;
    argoAppName?: string;
    argoInstance?: string;
    steps?: BackendStep[];
  } | null>(null);

  const stream = useTaskEventStream(taskId!);
  const { completed, cancelled: taskCancelled, loading, task, output, stepLogs, error } = stream;

  const steps = useMemo(
    () => task?.spec.steps.map(step => ({ ...step, ...(stream.steps?.[step.id] ?? {}) })) ?? [],
    [stream, task],
  );

  useEffect(() => {
    const active = steps.find(s => ['failed', 'processing'].includes(s.status));
    if (completed && !active) setSelectedStepId(steps[steps.length - 1]?.id);
    else setSelectedStepId(active?.id);
  }, [steps, completed]);

  const logText = useMemo(() => {
    if (!selectedStepId) return 'Waiting for task to start...';
    const log = stepLogs[selectedStepId];
    return log?.length ? log.join('\n') : 'Waiting for logs...';
  }, [stepLogs, selectedStepId]);

  const taskNotFound = completed && !loading && !task;
  const taskStatus = task?.status ?? (loading ? 'processing' : 'open');
  const parameters = task?.spec?.parameters as Record<string, unknown> | undefined;

  const prUrl = useMemo(() => {
    const fromLinks = output?.links?.map(l => extractGithubUrl(l.url, 'pull')).find(Boolean);
    if (fromLinks) return fromLinks;
    const log = Object.values(stepLogs).flat().join('\n');
    return log.match(/https:\/\/github\.com\/[^\s/]+\/[^\s/]+\/pull\/\d+/)?.[0];
  }, [output, stepLogs]);

  const actionsRunUrl = useMemo(() => {
    const fromLinks = output?.links?.map(l => extractGithubUrl(l.url, 'actions')).find(Boolean);
    if (fromLinks) return fromLinks;
    const log = Object.values(stepLogs).flat().join('\n');
    return log.match(/https:\/\/github\.com\/[^\s/]+\/[^\s/]+\/actions\/runs\/\d+/)?.[0];
  }, [output, stepLogs]);

  const entityRef   = task?.spec?.templateInfo?.entityRef;
  const argoAppName  = output?.argoAppName  as string | undefined;
  const argoInstance = output?.argoInstance as string | undefined;
  const platform     = output?.platform     as string | undefined;

  useEffect(() => {
    if (!completed || backendFallback !== null) return;
    if (actionsRunUrl && argoAppName) return;
    discoveryApi.getBaseUrl('tasks').then(base =>
      fetchApi.fetch(`${base}/user-tasks/${taskId}`)
    ).then(resp => {
      if (!resp?.ok) { setBackendFallback({}); return; }
      return resp.json().then((data: any) => setBackendFallback({
        prUrl: data.prUrl, actionsRunUrl: data.actionsRunUrl,
        argoAppName: data.argoAppName, argoInstance: data.argoInstance,
        steps: data.steps,
      }));
    }).catch(() => setBackendFallback({}));
  }, [completed, actionsRunUrl, argoAppName, backendFallback, discoveryApi, fetchApi, taskId]);

  const effectivePrUrl         = prUrl         ?? backendFallback?.prUrl;
  const effectiveActionsRunUrl = actionsRunUrl ?? backendFallback?.actionsRunUrl;
  const effectiveArgoAppName   = argoAppName   ?? backendFallback?.argoAppName;
  const effectiveArgoInstance  = argoInstance  ?? backendFallback?.argoInstance;

  return (
    <Page themeId="home">
      <Header pageTitleOverride={`Task ${taskId}`} title="Task Activity" subtitle={`Task: ${taskId}`} />
      <Content>
        <Box style={{ background: S.bg, minHeight: '100%' }} p={3}>
          {taskNotFound ? (
            <Box p={4}>
              <Typography style={{ color: S.red, fontWeight: 600, marginBottom: 16 }}>Task not found</Typography>
              <StripeBtn onClick={() => navigate(rootPath())}>Back to Templates</StripeBtn>
            </Box>
          ) : (
            <>
              {/* ── Page header ── */}
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5} mb={3}>
                <Typography style={{ color: S.text, fontWeight: 700, fontSize: 20 }}>Task</Typography>
                <Tooltip title={taskId ?? ''} placement="top">
                  <Typography style={{ color: S.textMuted, fontSize: 13, fontFamily: S.mono, cursor: 'default' }}>
                    {taskId ? `${taskId.slice(0, 8)}…` : ''}
                  </Typography>
                </Tooltip>
                <StatusPill status={taskStatus} />
                {task?.createdAt && (
                  <Typography style={{ color: S.textMuted, fontSize: 13 }}>
                    Started {DateTime.fromISO(task.createdAt).toRelative()}
                  </Typography>
                )}
                {output?.links?.map((link, i) => (
                  <Link key={i} to={link.url ?? ''} target="_blank">
                    <span style={{ fontSize: 13 }}>{link.title ?? link.url}</span>
                  </Link>
                ))}
              </Box>

              {/* ── Steps + log terminal ── */}
              {loading && steps.length === 0 ? (
                <Progress />
              ) : (
                <Grid container spacing={2} style={{ marginBottom: 24 }}>
                  {/* Left: step timeline */}
                  <Grid item xs={12} md={3}>
                    <Card style={{ height: '100%' }}>
                      <CardHeader
                        label="Steps"
                        right={
                          <Typography style={{ color: S.textMuted, fontSize: 11, fontFamily: S.mono }}>
                            {steps.filter(s => s.status === 'completed').length}/{steps.length}
                          </Typography>
                        }
                      />
                      {/* Step timeline with connecting line */}
                      <Box style={{ position: 'relative' }}>
                        {steps.length > 1 && (
                          <Box style={{
                            position: 'absolute',
                            left: 21,   // 14px padding + 8px (half of 16px dot) - 1px (half line width)
                            top: 18,    // center of first dot
                            bottom: 18, // center of last dot
                            width: 1,
                            background: S.border,
                            zIndex: 0,
                          }} />
                        )}
                        {steps.map((step, idx) => {
                          const isSelected = step.id === selectedStepId;
                          return (
                            <Box
                              key={idx}
                              onClick={() => setSelectedStepId(step.id)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '10px 14px',
                                background: isSelected ? '#f0f4ff' : 'transparent',
                                borderLeft: `3px solid ${isSelected ? S.accent : 'transparent'}`,
                                borderBottom: idx < steps.length - 1 ? `1px solid ${S.border}` : 'none',
                                cursor: 'pointer', transition: 'background 0.12s',
                                position: 'relative', zIndex: 1,
                              }}
                            >
                              {/* dot on a bg chip so it masks the line */}
                              <Box style={{
                                flexShrink: 0,
                                background: isSelected ? '#f0f4ff' : S.white,
                                borderRadius: '50%', display: 'flex',
                              }}>
                                <StepDot status={step.status} />
                              </Box>
                              <Box flex={1} minWidth={0}>
                                <Typography style={{
                                  color: isSelected ? S.accent : S.text,
                                  fontSize: 13, fontWeight: isSelected ? 600 : 400,
                                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                }}>
                                  {step.name}
                                </Typography>
                                <StepTimeTicker startedAt={step.startedAt} endedAt={step.endedAt} />
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                      <Box p={1.5} display="flex" flexDirection="column" gap={1} style={{ borderTop: `1px solid ${S.border}` }}>
                        <StripeBtn disabled={!completed} onClick={() => navigate(rootPath())}>
                          Create Another
                        </StripeBtn>
                        <StripeBtn variant="danger" disabled={completed || taskCancelled || cancelling}
                          onClick={async () => { setCancelling(true); await scaffolderApi.cancelTask(taskId!); }}
                        >
                          {cancelling || taskCancelled ? 'Cancelling…' : 'Cancel'}
                        </StripeBtn>
                      </Box>
                    </Card>
                  </Grid>

                  {/* Right: log terminal */}
                  <Grid item xs={12} md={9}>
                    {error && (
                      <Box mb={1.5} p={1.5} style={{ background: S.redBg, border: `1px solid #fca5a5`, borderRadius: S.radiusSm }}>
                        <Typography style={{ color: S.redText, fontSize: 13 }}>{error.message}</Typography>
                      </Box>
                    )}
                    {(() => {
                      const activeStep = steps.find(s => s.id === selectedStepId);
                      return (
                        <Box style={{
                          background: '#0d1117', borderRadius: S.radius,
                          border: '1px solid #30363d', height: '65vh',
                          display: 'flex', flexDirection: 'column', overflow: 'hidden',
                        }}>
                          {activeStep && (
                            <TerminalBar
                              stepName={activeStep.name}
                              status={activeStep.status ?? 'open'}
                              ticker={<StepTimeTicker startedAt={activeStep.startedAt} endedAt={activeStep.endedAt} />}
                            />
                          )}
                          <Box style={{ flex: 1, overflow: 'hidden' }}>
                            {!selectedStepId ? (
                              <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                                <CircularProgress size={22} style={{ color: '#58a6ff' }} />
                              </Box>
                            ) : (
                              <LogViewer text={logText} />
                            )}
                          </Box>
                        </Box>
                      );
                    })()}
                  </Grid>
                </Grid>
              )}

              {/* ── Deployment pipeline ── */}
              {(taskStatus === 'completed' || effectivePrUrl || effectiveActionsRunUrl) && (
                <Box mb={3}>
                  <DeploymentPipeline
                    taskStatus={taskStatus}
                    prUrl={effectivePrUrl}
                    actionsRunUrl={effectiveActionsRunUrl}
                    entityRef={entityRef}
                    argoAppName={effectiveArgoAppName}
                    argoInstance={effectiveArgoInstance}
                    platform={platform}
                  />
                </Box>
              )}

              {/* ── Input parameters ── */}
              {parameters && Object.keys(parameters).length > 0 && (
                <Box mb={3}>
                  <Card>
                    <CardHeader label="Input Parameters" />
                    {Object.entries(parameters).map(([key, val], idx, arr) => (
                      <Box key={key} display="flex" alignItems="flex-start" px={2} py={1.5}
                        style={{ borderBottom: idx < arr.length - 1 ? `1px solid ${S.border}` : 'none' }}
                      >
                        <Typography style={{ width: '28%', color: S.textSub, fontSize: 13, fontWeight: 500, paddingRight: 16, paddingTop: 2, flexShrink: 0 }}>
                          {key}
                        </Typography>
                        <Box flex={1} style={{ color: S.text, fontSize: 13 }}>
                          {renderValue(val)}
                        </Box>
                      </Box>
                    ))}
                  </Card>
                </Box>
              )}

              {/* ── Execution Steps (post-completion, from backend) ── */}
              {completed && backendFallback?.steps && backendFallback.steps.length > 0 && (
                <Box>
                  <Card>
                    <CardHeader
                      label="Execution Steps"
                      right={
                        <Typography style={{ color: S.textMuted, fontSize: 11, fontFamily: S.mono }}>
                          {backendFallback.steps.filter(s => s.status === 'completed').length}/{backendFallback.steps.length}
                        </Typography>
                      }
                    />
                    {backendFallback.steps.map((step, idx) => (
                      <Accordion key={step.id} defaultExpanded={step.status === 'failed'}
                        elevation={0} disableGutters
                        style={{ borderBottom: idx < backendFallback.steps!.length - 1 ? `1px solid ${S.border}` : 'none' }}
                        sx={{ '&:before': { display: 'none' } }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon style={{ color: S.textMuted, fontSize: 18 }} />}
                          style={{ minHeight: 48, padding: '0 14px' }}
                        >
                          <Box display="flex" alignItems="center" width="100%" gap={1.5}>
                            <StepDot status={step.status} />
                            <Typography style={{ color: S.text, fontSize: 14, fontWeight: 500 }}>
                              {step.name}
                            </Typography>
                            <Box flexGrow={1} />
                            <StatusPill status={step.status} />
                            <Typography style={{ color: S.textMuted, fontSize: 12, fontFamily: S.mono, minWidth: 48, textAlign: 'right', marginRight: 4 }}>
                              {formatMs(step.duration, step.startTime, step.endTime)}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails style={{ padding: '0 14px 16px', borderTop: `1px solid ${S.border}` }}>
                          <Box pt={2}>
                            <Box mb={2} display="flex" flexWrap="wrap" gap={3}>
                              <Typography style={{ color: S.textSub, fontSize: 12 }}>
                                <strong>Started:</strong> {new Date(step.startTime).toLocaleString()}
                              </Typography>
                              {step.endTime && (
                                <Typography style={{ color: S.textSub, fontSize: 12 }}>
                                  <strong>Completed:</strong> {new Date(step.endTime).toLocaleString()}
                                </Typography>
                              )}
                            </Box>
                            {step.error && (
                              <Box mb={2} p={1.5} style={{ background: S.redBg, border: `1px solid #fca5a5`, borderRadius: S.radiusSm }}>
                                <Typography style={{ color: S.redText, fontSize: 13 }}>Error: {step.error}</Typography>
                              </Box>
                            )}
                            <CodeSnippet language="bash" text={step.output} showLineNumbers />
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Card>
                </Box>
              )}
            </>
          )}
        </Box>
      </Content>
    </Page>
  );
};
